/**
 * Selekt - select replacement library, entry point.
 *
 * Usage:
 *   g.selekt('select[selekt]');            // init all (returns instances)
 *   g.selekt('#country', { ... });         // init one with options
 *   g.selekt.get('#country');              // existing instance
 *   el.selekt                              // same instance, from the element
 *
 * Options resolution (later wins): g.selekt.defaults < init options <
 * `selekt-*` element attributes. Attributes are flat scalars (no JSON):
 * kebab-case maps to camelCase (selekt-per-page="50" -> perPage: 50),
 * "true"/"false" become booleans, numbers become numbers, and the special
 * family selekt-param-<name>="v" fills options.params.<name>. dependsOn is
 * expressed flat: selekt-depends-on="#country" selekt-depends-param="countryId"
 * [selekt-keep-value].
 *
 * Depends on genus (g.fetch for endpoint sources, g.error for system errors).
 * Full reference: docs/libs/selekt.md.
 */
import { Selekt, coerce } from './selekt.js';

// Bump on every meaningful change to the library.
const VERSION = '0.4.0';

const defaults = {
	mode: 'simple',        // simple | complex | modal | switch
	multiple: null,        // null = take it from the <select multiple> attribute
	// null = the standard word — resolved at the render sites as a
	// literal t('Select...') so the scanner sees it (i18n hygiene);
	// a project-provided placeholder translates as project data
	placeholder: null,
	items: null,           // local dataset; null = parse the original <option> tree
	endpoint: '',          // API list endpoint -> ajax source through g.fetch
	params: {},            // business filters sent in data ({ ...params })
	perPage: 25,
	searchThreshold: 8,    // simple+single: search appears at >= this many options
	searchDebounce: 250,   // ms, ajax sources only
	clearable: true,
	maxTagsVisible: false, // multiple: false = show all tags, 0 = only "N items selected", N = collapse to "+x more"
	controlsAlign: 'top',  // multiple, multi-row: clear x + caret on the first ('top') or last ('bottom') row
	create: false,         // false | true (label becomes the value) | '/create/endpoint'
	createDelimiters: '',  // create: characters that commit the typed text as a tag + split a pasted list (',;')
	selectChildren: false, // multiple: selecting a parent selects its subtree
	dependsOn: null,       // { source: '#css-selector', param: 'name' }
	keepValue: false,      // dependsOn: keep the value if still present in the new set
};

const camel = (s) => s.replace(/-([a-z])/g, (m, c) => c.toUpperCase());

// selekt-* / data-selekt-* attributes -> options (highest priority).
function attrOptions(el) {
	const opts = {};
	const params = {};
	for (const attr of el.attributes) {
		let name = null;
		if (attr.name.startsWith('data-selekt-')) name = attr.name.slice(12);
		else if (attr.name.startsWith('selekt-')) name = attr.name.slice(7);
		if (!name) continue;
		const value = attr.value === '' ? true : coerce(attr.value);
		if (name.startsWith('param-')) params[camel(name.slice(6))] = value;
		else opts[camel(name)] = value;
	}
	if (Object.keys(params).length) opts.params = params;
	return opts;
}

function buildOptions(el, options) {
	const merged = { ...defaults, ...options, ...attrOptions(el) };
	if (merged.params && options && options.params) merged.params = { ...options.params, ...attrOptions(el).params };
	// flat depends attributes -> the dependsOn object
	if (typeof merged.dependsOn === 'string' && merged.dependsOn) {
		merged.dependsOn = { source: merged.dependsOn, param: merged.dependsParam || '' };
	}
	return merged;
}

function selekt(target, options) {
	let els;
	if (typeof target === 'string') els = Array.from(document.querySelectorAll(target));
	else if (target instanceof Element) els = [target];
	else els = Array.from(target || []);

	const instances = els.map((el) => el.selekt || new Selekt(el, buildOptions(el, options)));
	return instances.length === 1 ? instances[0] : instances;
}

selekt.version = () => VERSION;
selekt.defaults = defaults;
selekt.get = (target) => {
	const el = typeof target === 'string' ? document.querySelector(target) : target;
	return (el && el.selekt) || null;
};

if (window.g) {
	window.g.selekt = selekt;
	window.g.registerLib('selekt');
} else {
	window.selekt = selekt; // genus not loaded (shouldn't happen in the template)
}
